import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { openCandidateDetailsModal } from "../utils/redux/candidateSlice";
import CandidateScoreReason from "./CandidateScoreReason";
import CandidateStatusUpdateModal from "./CandidateStatusUpdateModal";
import ScoreRing from "./common/ScoreRing";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faPhone,
  faClipboardCheck,
} from "@fortawesome/free-solid-svg-icons";
import JobPostingDescription from "./JobPostingDescription";
import i18next from "i18next";

/**
 * Extracts initials from a full name string.
 * E.g., "Jean Dupont" => "JD"
 */
const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? "?";
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const CandidateItem = ({ candidate, tenantId, jobUuid }) => {
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(i18next.language);

  const dispatch = useDispatch();
  const { t } = useTranslation("CandidateItem");

  useEffect(() => {
    const browserLanguage = navigator.language || navigator.userLanguage;
    const languageCode = browserLanguage.split("-")[0];
    if (
      languageCode !== i18next.language &&
      (languageCode === "en" || languageCode === "fr")
    ) {
      i18next.changeLanguage(languageCode);
      setCurrentLanguage(languageCode);
    }
  }, []);

  const handleOpenModal = () => {
    dispatch(openCandidateDetailsModal(candidate));
  };

  const handleOpenStatusModal = (event) => {
    event.stopPropagation();
    setIsStatusModalOpen(true);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "Offered":
      case "Offer":
        return "text-emerald-700 bg-emerald-50 border border-emerald-200";
      case "Rejected":
      case "Rejeter":
        return "text-red-700 bg-red-50 border border-red-200";
      case "Interview":
        return "text-blue-700 bg-blue-50 border border-blue-200";
      case "Evaluation":
        return "text-amber-700 bg-amber-50 border border-amber-200";
      default:
        return "text-slate-600 bg-slate-100 border border-slate-200";
    }
  };

  const totalScore = candidate.score_list?.total_score ?? 0;
  const isRejected = ["Rejeter", "Rejected"].includes(candidate.ApplicationStatus);

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer group"
      onClick={handleOpenModal}
    >
      <CandidateStatusUpdateModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        candidate={candidate}
        tenantId={tenantId}
        jobUuid={jobUuid}
      />

      <div className="p-5">
        {/* Header: Avatar + Name + Score Ring + Status Button */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {/* Avatar Circle */}
            <div
              className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold tracking-wide ${
                isRejected
                  ? "bg-red-100 text-red-700"
                  : "bg-[#0077B6]/10 text-[#0077B6]"
              }`}
            >
              {getInitials(candidate.FullName)}
            </div>

            <div>
              <h2
                className={`text-lg font-semibold leading-tight ${
                  isRejected ? "text-red-700" : "text-slate-900"
                }`}
              >
                {candidate.FullName}
              </h2>
              {/* Status pill inline */}
              <span
                className={`inline-block mt-1 text-xs font-medium px-2.5 py-0.5 rounded-full ${getStatusStyle(
                  candidate.ApplicationStatus
                )}`}
              >
                {candidate.ApplicationStatus}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Score Ring */}
            <ScoreRing score={totalScore} size={48} strokeWidth={4} />

            {/* Change Status Button */}
            <button
              onClick={handleOpenStatusModal}
              className="px-4 py-2 bg-[#A47713] hover:bg-[#8a6310] text-white font-semibold rounded-full text-xs transition-colors duration-200 whitespace-nowrap"
            >
              {t("candidateItem.changeStatus")}
            </button>
          </div>
        </div>

        {/* Body: Contact + Assessments */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <p className="text-sm text-slate-600 flex items-center gap-2">
              <FontAwesomeIcon icon={faEnvelope} className="text-slate-400 w-3.5" />
              <span className="truncate">
                {candidate.ContactInformation?.Email ?? "—"}
              </span>
            </p>
            <p className="text-sm text-slate-600 flex items-center gap-2">
              <FontAwesomeIcon icon={faPhone} className="text-slate-400 w-3.5" />
              <span>{candidate.ContactInformation?.Phone ?? "—"}</span>
            </p>

            {/* Score breakdown */}
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-sm text-slate-700 font-medium mb-1">
                {t("candidateItem.ranking")}: {totalScore}/100
              </p>
              <div className="text-xs text-slate-500">
                <JobPostingDescription
                  description={candidate.score_list?.summary?.[i18next.languages[0]]}
                />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {candidate.assessmentNames?.map((assessment) => (
              <div
                key={assessment.link}
                className="bg-slate-50 rounded-xl p-3 border border-slate-100"
              >
                <p className="text-sm text-slate-700 flex items-center gap-2 mb-1">
                  <FontAwesomeIcon
                    icon={faClipboardCheck}
                    className="text-slate-400 w-3.5"
                  />
                  <strong>{assessment.title}</strong>
                </p>
                <div className="flex items-center justify-between">
                  <ScoreRing score={assessment.score} size={32} strokeWidth={3} />
                  <a
                    className="text-xs text-[#0077B6] hover:underline font-medium"
                    href={`${process.env.REACT_APP_BASE_URL}assessmentresults/${assessment.link}`}
                    rel="noreferrer"
                    target="_blank"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t("candidateItem.assessmentResult")}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tags row */}
        {candidate.tags && candidate.tags.length > 0 && (
          <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap gap-1.5">
            {candidate.tags.map((tag, idx) => (
              <span
                key={idx}
                className="text-xs font-medium px-2.5 py-1 rounded-full bg-[#A47713]/10 text-[#A47713] border border-[#A47713]/20"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

CandidateItem.propTypes = {
  candidate: PropTypes.shape({
    FullName: PropTypes.string.isRequired,
    ApplicationStatus: PropTypes.string.isRequired,
    ContactInformation: PropTypes.shape({
      Email: PropTypes.string,
      Phone: PropTypes.string,
    }),
    score_list: PropTypes.object.isRequired,
    assessmentNames: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        score: PropTypes.number.isRequired,
        link: PropTypes.string.isRequired,
        description: PropTypes.string,
      })
    ),
    tags: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  tenantId: PropTypes.string.isRequired,
  jobUuid: PropTypes.string.isRequired,
};

export default CandidateItem;
